import type { AdminBoardSummary, BoardVisibility } from '@molt/shared';
import { Link } from '@tanstack/react-router';

import { RelativeTime } from '@/components/RelativeTime';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  ADMIN_TD_CLASS,
  ADMIN_TH_CLASS,
  ADMIN_THEAD_CLASS,
  ADMIN_TR_CLASS,
  AdminPill,
  AdminTableFrame,
} from './AdminConsole';

/**
 * SA-9 — the Boards tab. **Titles only, never card content** (SA-8). Each title
 * links to the board face by its short link: `/b/<shortLink>` without a slug,
 * which the board route corrects to its canonical path in one hop
 * (`FR-EXP-015` AC4), so the console never spells a slug of its own.
 *
 * Opening a board from here is an ordinary navigation: the board face records
 * `super.board_opened` server-side and shows its platform-admin banner itself.
 */

/** Key suffix = wire value; `admin-console.routing.test.ts` iterates `BOARD_VISIBILITIES`. */
export function adminVisibilityKey(visibility: BoardVisibility): string {
  return `admin.visibility.${visibility}`;
}

export interface AdminBoardTableProps {
  readonly items: readonly AdminBoardSummary[];
}

export function AdminBoardTable({ items }: AdminBoardTableProps) {
  const { t } = useInterfaceLanguage();
  return (
    <AdminTableFrame label={t('admin.boards.tableLabel')}>
      <thead className={ADMIN_THEAD_CLASS}>
        <tr>
          <th className={ADMIN_TH_CLASS}>{t('admin.boards.column.title')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.boards.column.workspace')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.boards.column.visibility')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.boards.column.status')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.boards.column.members')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.boards.column.lastActivity')}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((board) => (
          <tr key={board.id} className={ADMIN_TR_CLASS}>
            <td className={ADMIN_TD_CLASS}>
              <Link
                to="/b/$shortLink/{-$slug}"
                params={{ shortLink: board.shortLink }}
                className="font-medium text-ink underline-offset-2 hover:underline"
              >
                {board.title}
              </Link>
            </td>
            <td className={`${ADMIN_TD_CLASS} text-ink`}>{board.workspaceName}</td>
            <td className={ADMIN_TD_CLASS}>
              <AdminPill>{t(adminVisibilityKey(board.visibility))}</AdminPill>
            </td>
            <td className={ADMIN_TD_CLASS}>
              {board.closed ? (
                <AdminPill tone="warn">{t('admin.boards.status.closed')}</AdminPill>
              ) : (
                <span className="whitespace-nowrap text-ink-muted">
                  {t('admin.boards.status.open')}
                </span>
              )}
            </td>
            <td className={`${ADMIN_TD_CLASS} tabular-nums text-ink`}>
              {board.memberCount}
            </td>
            <td className={`${ADMIN_TD_CLASS} whitespace-nowrap text-ink-muted`}>
              {board.lastActivityAt === null ? (
                t('admin.boards.noActivity')
              ) : (
                <RelativeTime value={board.lastActivityAt} />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </AdminTableFrame>
  );
}
